import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import AdminLayout from "@/components/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, Sparkles, BrainCircuit, FileUp, Copy, Check, AlertCircle } from "lucide-react";
import { BlogPreviewDialog } from "@/components/admin/BlogPreviewDialog";
import { BatchBlogGenerator } from "@/components/admin/BatchBlogGenerator";
import { RoadmapEditor } from "@/components/admin/RoadmapEditor";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface Category {
  id: string;
  name: string;
}

const EditBlogPost = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [previewData, setPreviewData] = useState<any>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [generateImage, setGenerateImage] = useState(true);
  const [bulkImportText, setBulkImportText] = useState("");
  const [parsedData, setParsedData] = useState<any>(null);
  const [parseErrors, setParseErrors] = useState<string[]>([]);
  const [showInstructions, setShowInstructions] = useState(true);
  const [copiedTemplate, setCopiedTemplate] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    slug: "",
    excerpt: "",
    content: "",
    category_id: "",
    featured_image: "",
    video_url: "",
    status: "draft",
    read_time: "5 min read",
  });

  useEffect(() => {
    const initialize = async () => {
      await fetchCategories();
      if (id && id !== "new") {
        await fetchPost();
      }
    };

    initialize();
  }, [id]);

  const fetchCategories = async () => {
    const { data } = await supabase
      .from("categories")
      .select("id, name")
      .order("name");
    if (data) setCategories(data);
  };

  const fetchPost = async () => {
    try {
      const { data, error } = await supabase
        .from("blog_posts")
        .select("*")
        .eq("id", id)
        .maybeSingle();

      if (error) throw error;
      
      if (data) {
        setFormData({
          title: data.title || "",
          slug: data.slug || "",
          excerpt: data.excerpt || "",
          content: data.content || "",
          category_id: data.category_id || "",
          featured_image: data.featured_image || "",
          video_url: data.video_url || "",
          status: data.status || "draft",
          read_time: data.read_time || "5 min read",
        });
      }
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load blog post.",
      });
      navigate("/admin/posts");
    }
  };

  const generateSlug = (title: string) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "");
  };

  const parseBulkImport = (text: string) => {
    const errors: string[] = [];
    const parsed: any = {};

    try {
      // Split into metadata and content sections
      const contentSeparator = "---CONTENT---";
      const parts = text.split(contentSeparator);
      
      if (parts.length < 2) {
        errors.push("Missing '---CONTENT---' separator. Please include it between metadata and content.");
        return { parsed: null, errors };
      }

      const metadataSection = parts[0].trim();
      const contentSection = parts.slice(1).join(contentSeparator).trim();

      // Parse metadata lines
      const lines = metadataSection.split("\n");
      lines.forEach((line) => {
        const colonMatch = line.match(/^([^:]+):\s*(.*)$/);
        const equalsMatch = line.match(/^([^=]+)=\s*(.*)$/);
        const match = colonMatch || equalsMatch;

        if (match) {
          const key = match[1].trim().toLowerCase().replace(/\s+/g, "_");
          const value = match[2].trim();

          switch (key) {
            case "title":
              parsed.title = value;
              break;
            case "slug":
              parsed.slug = value;
              break;
            case "category":
              parsed.categoryName = value;
              break;
            case "excerpt":
              parsed.excerpt = value;
              break;
            case "video_url":
            case "video":
              parsed.video_url = value;
              break;
            case "featured_image":
            case "image":
              parsed.featured_image = value;
              break;
            case "read_time":
            case "readtime":
              parsed.read_time = value;
              break;
            case "status":
              parsed.status = value.toLowerCase();
              break;
          }
        }
      });

      // Set content
      parsed.content = contentSection;

      // Validate required fields
      if (!parsed.title) {
        errors.push("Title is required");
      }
      if (!parsed.content) {
        errors.push("Content is required");
      }

      // Auto-generate slug if not provided
      if (parsed.title && !parsed.slug) {
        parsed.slug = generateSlug(parsed.title);
      }

      // Default status if not provided
      if (!parsed.status) {
        parsed.status = "draft";
      }

      // Match category by name
      if (parsed.categoryName) {
        const matchedCategory = categories.find(
          (cat) => cat.name.toLowerCase() === parsed.categoryName.toLowerCase()
        );
        if (matchedCategory) {
          parsed.category_id = matchedCategory.id;
        } else {
          errors.push(`Category "${parsed.categoryName}" not found. Please select from existing categories.`);
        }
      }

      return { parsed, errors };
    } catch (error: any) {
      errors.push(`Parse error: ${error.message}`);
      return { parsed: null, errors };
    }
  };

  const handleParseBulkImport = () => {
    if (!bulkImportText.trim()) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Please paste your blog post content first.",
      });
      return;
    }

    const { parsed, errors } = parseBulkImport(bulkImportText);
    setParseErrors(errors);

    if (parsed && errors.length === 0) {
      setParsedData(parsed);
      toast({
        title: "Success",
        description: "Blog post parsed successfully!",
      });
    } else if (parsed && errors.length > 0) {
      setParsedData(parsed);
      toast({
        variant: "destructive",
        title: "Parsed with warnings",
        description: `Found ${errors.length} issue(s). Please review.`,
      });
    } else {
      setParsedData(null);
      toast({
        variant: "destructive",
        title: "Parse failed",
        description: "Unable to parse the blog post. Check the format.",
      });
    }
  };

  const handleUseParsedData = (switchTab: boolean = false) => {
    if (!parsedData) return;

    setFormData({
      title: parsedData.title || "",
      slug: parsedData.slug || "",
      excerpt: parsedData.excerpt || "",
      content: parsedData.content || "",
      category_id: parsedData.category_id || "",
      featured_image: parsedData.featured_image || "",
      video_url: parsedData.video_url || "",
      status: parsedData.status || "draft",
      read_time: parsedData.read_time || "5 min read",
    });

    toast({
      title: "Data imported",
      description: "Blog post data has been populated in the form.",
    });

    if (switchTab) {
      // Switch to single post tab
      const singleTab = document.querySelector('[data-tab="single"]') as HTMLElement;
      if (singleTab) singleTab.click();
    }
  };

  const handleClearBulkImport = () => {
    setBulkImportText("");
    setParsedData(null);
    setParseErrors([]);
  };

  const copyTemplate = () => {
    const template = `Title: Your Blog Post Title Here
Slug: your-blog-post-slug
Category: Technology
Excerpt: A brief description of your blog post (1-2 sentences)
Video URL: https://youtube.com/watch?v=example
Featured Image: https://example.com/image.jpg
Read Time: 8 min read
Status: draft

---CONTENT---
# Introduction

Your full blog content goes here...

## Section 1

You can use markdown formatting...

## Section 2

Multiple paragraphs and sections...`;

    navigator.clipboard.writeText(template);
    setCopiedTemplate(true);
    setTimeout(() => setCopiedTemplate(false), 2000);
    toast({
      title: "Template copied",
      description: "Paste it in your text editor and fill in your content.",
    });
  };

  const extractYouTubeId = (url: string): string | null => {
    if (!url) return null;
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\?\/]+)/,
      /^([a-zA-Z0-9_-]{11})$/,
    ];
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }
    return null;
  };

  const getYouTubeThumbnail = (url: string): string => {
    const videoId = extractYouTubeId(url);
    return videoId ? `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg` : "";
  };

  const handleGenerateFromVideo = async () => {
    if (!formData.video_url) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Please enter a YouTube video URL first.",
      });
      return;
    }

    setAiGenerating(true);
    
    try {
      const { data, error } = await supabase.functions.invoke('generate-blog-from-video', {
        body: { videoUrl: formData.video_url, generateImage }
      });

      if (error) throw error;

      if (data.error) {
        throw new Error(data.error);
      }

      // Show preview dialog with generated content
      setPreviewData(data);
      setShowPreview(true);

    } catch (error: any) {
      console.error('Error generating blog:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to generate blog content. Please try again.",
      });
    } finally {
      setAiGenerating(false);
    }
  };

  const handleAcceptPreview = (data: any) => {
    // Populate form with AI-generated content
    setFormData(prev => ({
      ...prev,
      title: data.title,
      slug: data.slug,
      excerpt: data.excerpt,
      content: data.content,
      read_time: data.readTime,
      featured_image: data.featuredImage || prev.featured_image || getYouTubeThumbnail(prev.video_url),
    }));

    setShowPreview(false);

    toast({
      title: "Success!",
      description: `Blog post loaded successfully! Category suggestion: ${data.suggestedCategory}`,
    });
  };

  const handleBatchBlogGenerated = (data: any) => {
    setPreviewData(data);
    setShowPreview(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validation
      if (!formData.title.trim()) {
        throw new Error("Title is required");
      }
      if (!formData.content.trim()) {
        throw new Error("Content is required");
      }

      // Get user
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error("Not authenticated. Please log in again.");
      }

      const postData = {
        ...formData,
        slug: formData.slug || generateSlug(formData.title),
        author_id: user.id,
        published_at: formData.status === "published" ? new Date().toISOString() : null,
      };

      if (id === "new") {
        const { error } = await supabase
          .from("blog_posts")
          .insert([postData])
          .select();
        
        if (error) throw error;
        
        toast({
          title: "Success",
          description: "Blog post created successfully.",
        });
      } else {
        const { error } = await supabase
          .from("blog_posts")
          .update(postData)
          .eq("id", id)
          .select();
          
        if (error) throw error;
        
        toast({
          title: "Success",
          description: "Blog post updated successfully.",
        });
      }

      navigate("/admin/posts");
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to save blog post.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6 max-w-4xl">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/admin/posts")}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold">
              {id === "new" ? "Create" : "Edit"} Blog Post
            </h1>
          </div>
        </div>

        <Tabs defaultValue="single" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="single" data-tab="single">Single Post</TabsTrigger>
          <TabsTrigger value="bulk">
            <FileUp className="w-4 h-4 mr-2" />
            Bulk Import
          </TabsTrigger>
          <TabsTrigger value="roadmap" disabled={id === "new"}>
            28-Day Roadmap
          </TabsTrigger>
          <TabsTrigger value="batch">Batch Generator</TabsTrigger>
        </TabsList>

          <TabsContent value="single" className="space-y-6">
            <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="title">Title *</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => {
                setFormData({
                  ...formData,
                  title: e.target.value,
                  slug: generateSlug(e.target.value),
                });
              }}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="slug">Slug</Label>
            <Input
              id="slug"
              value={formData.slug}
              onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="excerpt">Excerpt</Label>
            <Textarea
              id="excerpt"
              value={formData.excerpt}
              onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
              rows={3}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="content">Content *</Label>
            <Textarea
              id="content"
              value={formData.content}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              rows={15}
              required
            />
          </div>

          <div className="grid gap-6 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="category">Category</Label>
              <Select
                value={formData.category_id}
                onValueChange={(value) =>
                  setFormData({ ...formData, category_id: value })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>
                      {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select
                value={formData.status}
                onValueChange={(value) => setFormData({ ...formData, status: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="published">Published</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="featured_image">Featured Image URL</Label>
            <Input
              id="featured_image"
              value={formData.featured_image}
              onChange={(e) =>
                setFormData({ ...formData, featured_image: e.target.value })
              }
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="video_url">Video URL (YouTube)</Label>
            <div className="flex gap-2">
              <Input
                id="video_url"
                value={formData.video_url}
                onChange={(e) => {
                  const videoUrl = e.target.value;
                  const thumbnail = getYouTubeThumbnail(videoUrl);
                  setFormData({ 
                    ...formData, 
                    video_url: videoUrl,
                    featured_image: thumbnail || formData.featured_image
                  });
                }}
                placeholder="https://www.youtube.com/watch?v=..."
              />
              <Button
                type="button"
                onClick={handleGenerateFromVideo}
                disabled={aiGenerating || !formData.video_url}
                className="whitespace-nowrap"
              >
                <Sparkles className="mr-2 h-4 w-4" />
                {aiGenerating ? "Generating..." : "Generate with AI"}
              </Button>
            </div>
            <div className="flex items-center space-x-2 pt-2">
              <Switch
                id="generate-image"
                checked={generateImage}
                onCheckedChange={setGenerateImage}
                disabled={aiGenerating}
              />
              <Label htmlFor="generate-image" className="cursor-pointer text-sm">
                Generate custom AI featured image (takes extra 10-15 seconds)
              </Label>
            </div>
            {aiGenerating && (
              <p className="text-sm text-muted-foreground">
                Extracting transcript and generating blog content... This may take 15-30 seconds.
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="read_time">Read Time</Label>
            <Input
              id="read_time"
              value={formData.read_time}
              onChange={(e) => setFormData({ ...formData, read_time: e.target.value })}
              placeholder="5 min read"
            />
          </div>

          <div className="flex gap-4">
            <Button type="submit" disabled={loading}>
              {loading ? "Saving..." : id === "new" ? "Create Post" : "Update Post"}
            </Button>
            {id && id !== "new" && (
              <Button
                type="button"
                variant="secondary"
                onClick={() => navigate(`/admin/quiz/${id}`)}
              >
                <BrainCircuit className="mr-2 h-4 w-4" />
                Manage Quiz
              </Button>
            )}
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate("/admin/posts")}
            >
              Cancel
            </Button>
          </div>
        </form>
      </TabsContent>

      <TabsContent value="bulk" className="space-y-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column - Input & Instructions */}
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileUp className="w-5 h-5" />
                  Paste Your Blog Post
                </CardTitle>
                <CardDescription>
                  Paste your complete blog post in the format shown below
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Textarea
                  value={bulkImportText}
                  onChange={(e) => setBulkImportText(e.target.value)}
                  placeholder="Paste your formatted blog post here..."
                  className="min-h-[400px] font-mono text-sm"
                />
                <div className="flex gap-2">
                  <Button onClick={handleParseBulkImport} className="flex-1">
                    <Sparkles className="w-4 h-4 mr-2" />
                    Parse & Preview
                  </Button>
                  <Button onClick={handleClearBulkImport} variant="outline">
                    Clear
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Instructions */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">Format Instructions</CardTitle>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={copyTemplate}
                    className="h-8"
                  >
                    {copiedTemplate ? (
                      <>
                        <Check className="w-3 h-3 mr-1" />
                        Copied
                      </>
                    ) : (
                      <>
                        <Copy className="w-3 h-3 mr-1" />
                        Copy Template
                      </>
                    )}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-sm space-y-2 text-muted-foreground">
                  <p>Use this format for your blog post:</p>
                  <pre className="bg-muted p-3 rounded-md overflow-x-auto text-xs">
{`Title: Your Blog Post Title
Slug: your-blog-post-slug
Category: Category Name
Excerpt: Brief description
Video URL: https://youtube.com/...
Featured Image: https://...
Read Time: 5 min read
Status: draft

---CONTENT---
# Your Content Here

Write your full blog post content...`}
                  </pre>
                  <ul className="list-disc list-inside space-y-1 mt-3">
                    <li><strong>Required:</strong> Title, Content</li>
                    <li><strong>Optional:</strong> All other fields</li>
                    <li>Slug auto-generates from title if not provided</li>
                    <li>Status defaults to "draft"</li>
                    <li>Category must match an existing category name</li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Preview */}
          <div className="space-y-4">
            {parsedData ? (
              <>
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Check className="w-5 h-5 text-primary" />
                      Parsed Data Preview
                    </CardTitle>
                    <CardDescription>
                      Review the extracted data before importing
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {parseErrors.length > 0 && (
                      <Alert variant="destructive">
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription>
                          <ul className="list-disc list-inside space-y-1">
                            {parseErrors.map((error, idx) => (
                              <li key={idx}>{error}</li>
                            ))}
                          </ul>
                        </AlertDescription>
                      </Alert>
                    )}

                    <div className="space-y-2 text-sm">
                      <div className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-primary mt-0.5" />
                        <div className="flex-1">
                          <strong>Title:</strong>{" "}
                          <span className="text-muted-foreground">
                            {parsedData.title || "(not provided)"}
                          </span>
                        </div>
                      </div>

                      <div className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-primary mt-0.5" />
                        <div className="flex-1">
                          <strong>Slug:</strong>{" "}
                          <span className="text-muted-foreground">
                            {parsedData.slug || "(auto-generated)"}
                          </span>
                        </div>
                      </div>

                      {parsedData.categoryName && (
                        <div className="flex items-start gap-2">
                          {parsedData.category_id ? (
                            <Check className="w-4 h-4 text-primary mt-0.5" />
                          ) : (
                            <AlertCircle className="w-4 h-4 text-destructive mt-0.5" />
                          )}
                          <div className="flex-1">
                            <strong>Category:</strong>{" "}
                            <span className="text-muted-foreground">
                              {parsedData.categoryName}
                              {!parsedData.category_id && " (not found)"}
                            </span>
                          </div>
                        </div>
                      )}

                      {parsedData.excerpt && (
                        <div className="flex items-start gap-2">
                          <Check className="w-4 h-4 text-primary mt-0.5" />
                          <div className="flex-1">
                            <strong>Excerpt:</strong>{" "}
                            <span className="text-muted-foreground">
                              {parsedData.excerpt.substring(0, 80)}
                              {parsedData.excerpt.length > 80 ? "..." : ""}
                            </span>
                          </div>
                        </div>
                      )}

                      {parsedData.video_url && (
                        <div className="flex items-start gap-2">
                          <Check className="w-4 h-4 text-primary mt-0.5" />
                          <div className="flex-1">
                            <strong>Video URL:</strong>{" "}
                            <span className="text-muted-foreground break-all">
                              {parsedData.video_url}
                            </span>
                          </div>
                        </div>
                      )}

                      {parsedData.featured_image && (
                        <div className="flex items-start gap-2">
                          <Check className="w-4 h-4 text-primary mt-0.5" />
                          <div className="flex-1">
                            <strong>Featured Image:</strong>{" "}
                            <span className="text-muted-foreground break-all">
                              {parsedData.featured_image}
                            </span>
                          </div>
                        </div>
                      )}

                      <div className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-primary mt-0.5" />
                        <div className="flex-1">
                          <strong>Read Time:</strong>{" "}
                          <span className="text-muted-foreground">
                            {parsedData.read_time || "5 min read"}
                          </span>
                        </div>
                      </div>

                      <div className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-primary mt-0.5" />
                        <div className="flex-1">
                          <strong>Status:</strong>{" "}
                          <span className="text-muted-foreground">
                            {parsedData.status || "draft"}
                          </span>
                        </div>
                      </div>

                      <div className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-primary mt-0.5" />
                        <div className="flex-1">
                          <strong>Content:</strong>{" "}
                          <span className="text-muted-foreground">
                            {parsedData.content?.substring(0, 100)}
                            {parsedData.content?.length > 100 ? "..." : ""}
                            {" "}
                            ({parsedData.content?.length || 0} characters)
                          </span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <div className="flex flex-col gap-2">
                  <Button
                    onClick={() => handleUseParsedData(true)}
                    size="lg"
                    className="w-full"
                    disabled={parseErrors.length > 0}
                  >
                    <Check className="w-4 h-4 mr-2" />
                    Use This Data & Edit
                  </Button>
                  <Button
                    onClick={() => handleUseParsedData(false)}
                    variant="outline"
                    size="lg"
                    className="w-full"
                    disabled={parseErrors.length > 0}
                  >
                    Import Without Switching
                  </Button>
                  <Button
                    onClick={handleClearBulkImport}
                    variant="ghost"
                    size="sm"
                    className="w-full"
                  >
                    Discard
                  </Button>
                </div>
              </>
            ) : (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-16 text-center">
                  <FileUp className="w-12 h-12 text-muted-foreground mb-4" />
                  <p className="text-muted-foreground mb-2">
                    Paste your blog post and click "Parse & Preview"
                  </p>
                  <p className="text-sm text-muted-foreground">
                    The preview will appear here
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </TabsContent>

      <TabsContent value="roadmap" className="space-y-6">
        <RoadmapEditor blogPostId={id || "new"} />
      </TabsContent>

      <TabsContent value="batch" className="space-y-6">
        <BatchBlogGenerator onBlogGenerated={handleBatchBlogGenerated} />
      </TabsContent>
    </Tabs>

    <BlogPreviewDialog
      open={showPreview}
      onOpenChange={setShowPreview}
      previewData={previewData}
      onAccept={handleAcceptPreview}
      onRegenerate={handleGenerateFromVideo}
      isRegenerating={aiGenerating}
    />
  </div>
</AdminLayout>
);
};

export default EditBlogPost;
